import {
  AgenticCallCitationIcon,
  AgenticEmailCitationIcon,
  AgenticTextCitationIcon,
  ApplicationQuestionsCitationIcon,
  CallCitationIcon,
  CommentsCitationIcon,
  EditCitationIcon,
  EmailCitationIcon,
  EvaluationCitationIcon,
  NoteCitationIcon,
  ResumeCitationIcon,
  StatusChangeCitationIcon,
  TextCitationIcon,
  type CitationSourceIcon,
} from "./CitationSourceIcons";

import { CommsScope, type UserSheetCommsTab } from "@/components/user-sheet/types";

import { dates } from "@/lib/dates";
import { formatting } from "@/lib/formatting";
import type { AiCallInsightCitation } from "@/types";

// The fields of a nova userPaperTrail.citationsForUser row this mapper reads.
// Structural on purpose: `detail` and `value` are jsonb on the wire, so both are
// narrowed here rather than trusted from the client types.
export interface PaperTrailCitationRow {
  detail?: unknown;
  entityField?: string | null;
  id: number;
  // Present only on shift citations, which the server joins onto the row.
  shift?: string;
  stafferId?: number | null;
  touchpointAt: string;
  // Null on edits, which is what keeps them from grouping.
  touchpointId?: string | null;
  // Wire enums, deliberately widened to string to defend against deploy skew.
  touchpointPrincipalType: string;
  touchpointType: string;
  value?: unknown;
}

// Where a citation's originating artifact lives: a comms-rail tab (with its
// scope) or a sheet tab. Unset when no surface hosts the artifact yet.
export type CitationDestination =
  | { scope: CommsScope; tab: UserSheetCommsTab; type: "comms" }
  | { tab: "notes" | "records"; type: "sheetTab" }
  // Another candidacy's sheet. Unlike the two above, this leaves the current
  // sheet, so it opens in a new tab rather than replacing what the staffer has.
  | {
      candidacyId: string;
      companyId: string;
      jobId: string;
      // Seeds the target sheet's tab via ?st=.
      sheetTab?: "records";
      type: "candidacy";
    };

// One thing an artifact said, and what that statement wrote. A text that states
// current and desired pay in the same breath yields two of these under one
// source; a shift statement that settles three shifts yields one.
export interface SpeakerThreadTurn {
  // USER is the candidate; INTERNAL is whoever was on our side.
  speaker: "INTERNAL" | "USER";
  text: string;
}

// One field this quote settled. Rendered as its own badge, so the parts stay
// separate rather than pre-joined into a display string.
export interface PaperTrailCitationChange {
  // Absent on edits, whose source label already names the field.
  label?: string;
  value: string;
}

export interface PaperTrailCitationClaim {
  // The values this quote wrote, one entry per field it settled.
  changes?: PaperTrailCitationChange[];
  id: number;
  // Application answers cite a form field rather than an utterance, so they
  // render as the pair instead of a bare quote.
  questionAnswer?: { answer: string; question: string };
  // Absent for provenance events with no artifact to quote (staffer edits,
  // resume/evaluation attribution). Those render as a bare value.
  quote?: string;
  // The exchange the quote sits inside, when the artifact is a conversation.
  thread?: SpeakerThreadTurn[];
}

export interface PaperTrailCitationSource {
  claims: PaperTrailCitationClaim[];
  destination?: CitationDestination;
  Icon: CitationSourceIcon;
  id: number;
  // What to call our side of a thread — the row's principal decides it.
  internalLabel: string;
  label: string;
  // Flags where the citation lives when that isn't the sheet being viewed. The
  // reason is the tooltip. Present with OR without a destination: an
  // other-candidacy citation is both tagged and navigable.
  tag?: string;
  tagReason?: string;
  // Epoch ms of touchpointAt. When the candidate said it.
  sortKey: number;
}

const SHORT_DATE_FORMAT = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  month: "short",
});

const COMMS_CANDIDATE_EMAIL: CitationDestination = {
  scope: CommsScope.CANDIDATE,
  tab: "email",
  type: "comms",
};
const COMMS_CANDIDATE_MESSAGES: CitationDestination = {
  scope: CommsScope.CANDIDATE,
  tab: "messages",
  type: "comms",
};
// Comments are the Internal rail's own sub-tab, not a message thread.
const COMMS_INTERNAL_COMMENTS: CitationDestination = {
  scope: CommsScope.INTERNAL,
  tab: "comments",
  type: "comms",
};
const SHEET_NOTES: CitationDestination = { tab: "notes", type: "sheetTab" };
const SHEET_RECORDS: CitationDestination = { tab: "records", type: "sheetTab" };

// An extraction row says what it is in its quote. An edit has no quote, so the
// field it changed has to be named outright — otherwise two edits to the two
// fields behind one display row are indistinguishable.
const FIELD_LABELS: Record<string, string> = {
  currentPay: "Current pay",
  desiredPay: "Desired pay",
  drugScreenStatus: "Drug screen",
  earliestStartDate: "Earliest start",
  hasCriminalRecord: "Criminal record",
  isOpenToRelocation: "Relocation",
  location: "Location",
  maxCommuteMiles: "Max commute",
  reasonForLeaving: "Reason for leaving",
};

// Shift rows all carry entityField "status", so the shift names them instead.
const SHIFT_LABELS: Record<string, string> = {
  FIRSTSHIFT: "1st shift",
  SECONDSHIFT: "2nd shift",
  THIRDSHIFT: "3rd shift",
  WEEKEND: "Weekends",
};

const DRUG_SCREEN_VALUE_LABELS: Record<string, string> = {
  WILL_NOT_PASS: "Won't pass",
  WILL_PASS: "Will pass",
  WILL_PASS_EX_THC: "Will pass (except THC)",
};

const SHIFT_STATUS_LABELS: Record<string, string> = {
  NO: "No",
  PREFERRED: "Preferred",
  YES: "Yes",
};

// Stance-major order, matching how the shift row itself reads: what they want
// leads, what they are open to follows, refusals trail. Shift order breaks ties.
const SHIFT_STATUS_ORDER = ["PREFERRED", "YES", "NO"];
const SHIFT_ORDER = ["FIRSTSHIFT", "SECONDSHIFT", "THIRDSHIFT", "WEEKEND"];

// Comments and applications belong to one specific candidacy, and the surfaces
// that host them are scoped to whichever candidacy the sheet is on. So a link is
// only correct when the citation is from this candidacy — otherwise it opens a
// different job's list and quietly shows the wrong thing.
const OTHER_CANDIDACY_REASON =
  "This happened on a different job's candidacy, so it isn't on this sheet.";
// Rows written before detail carried the ids can't be placed either way.
const UNKNOWN_CANDIDACY_REASON =
  "The paper trail didn't record which candidacy this came from, so it may or may not be this one.";

// Where a candidacy-scoped citation sits relative to the sheet being viewed.
const resolveCandidacy = (
  row: PaperTrailCitationRow,
  currentCandidacyId?: string,
  // Applications predate the detail ids: their touchpointId IS the candidacyId.
  fallbackCandidacyId?: string | null,
): "current" | "other" | "unknown" => {
  const candidacyId = getDetailCandidacyId(row.detail) ?? fallbackCandidacyId;
  if (!candidacyId || currentCandidacyId == null) return "unknown";
  return candidacyId === currentCandidacyId ? "current" : "other";
};

const sourceKind = (
  row: PaperTrailCitationRow,
  currentCandidacyId?: string,
): {
  destination?: CitationDestination;
  Icon: CitationSourceIcon;
  tag?: string;
  tagReason?: string;
  name: string;
} => {
  const isAgent = row.touchpointPrincipalType === "AGENT";
  switch (row.touchpointType) {
    case "CALL":
      return isAgent
        ? {
            destination: SHEET_RECORDS,
            Icon: AgenticCallCitationIcon,
            name: "AI call",
          }
        : {
            destination: SHEET_RECORDS,
            Icon: CallCitationIcon,
            name: "Call",
          };
    case "SMS":
      return isAgent
        ? {
            destination: COMMS_CANDIDATE_MESSAGES,
            Icon: AgenticTextCitationIcon,
            name: "AI text",
          }
        : {
            destination: COMMS_CANDIDATE_MESSAGES,
            Icon: TextCitationIcon,
            name: "Text",
          };
    case "EMAIL":
      return isAgent
        ? {
            destination: COMMS_CANDIDATE_EMAIL,
            Icon: AgenticEmailCitationIcon,
            name: "AI email",
          }
        : {
            destination: COMMS_CANDIDATE_EMAIL,
            Icon: EmailCitationIcon,
            name: "Email",
          };
    case "APPLICATION": {
      // Records shows the current candidacy's answers, so link only when this
      // citation is that candidacy.
      const scope = resolveCandidacy(row, currentCandidacyId, row.touchpointId);
      if (scope === "current") {
        return {
          destination: SHEET_RECORDS,
          Icon: ApplicationQuestionsCitationIcon,
          name: "Application answer",
        };
      }
      // Another candidacy: land on its Records tab, where its answers live.
      const otherCandidacy = getDetailCandidacyScope(row.detail);
      if (scope === "other" && otherCandidacy) {
        return {
          destination: {
            ...otherCandidacy,
            sheetTab: "records",
            type: "candidacy",
          },
          Icon: ApplicationQuestionsCitationIcon,
          name: "Application answer",
          tag: "Other candidacy",
          tagReason: OTHER_CANDIDACY_REASON,
        };
      }
      return {
        Icon: ApplicationQuestionsCitationIcon,
        name: "Application answer",
        ...(scope === "other"
          ? { tag: "Other candidacy", tagReason: OTHER_CANDIDACY_REASON }
          : { tag: "Candidacy unknown", tagReason: UNKNOWN_CANDIDACY_REASON }),
      };
    }
    case "INTERNAL_CANDIDACY_COMMENT": {
      // The internal rail fetches comments by the sheet's candidacyId, so the
      // comment is only in that list when the ids match.
      const scope = resolveCandidacy(row, currentCandidacyId);
      if (scope === "current") {
        return {
          destination: COMMS_INTERNAL_COMMENTS,
          Icon: CommentsCitationIcon,
          name: "Comment",
        };
      }
      // Another candidacy: no sheetTab — the comms rail opens with the sheet,
      // so the comment is reachable without seeding a tab.
      const otherCandidacy = getDetailCandidacyScope(row.detail);
      if (scope === "other" && otherCandidacy) {
        return {
          destination: { ...otherCandidacy, type: "candidacy" },
          Icon: CommentsCitationIcon,
          name: "Comment",
          tag: "Other candidacy",
          tagReason: OTHER_CANDIDACY_REASON,
        };
      }
      return {
        Icon: CommentsCitationIcon,
        name: "Comment",
        ...(scope === "other"
          ? { tag: "Other candidacy", tagReason: OTHER_CANDIDACY_REASON }
          : { tag: "Candidacy unknown", tagReason: UNKNOWN_CANDIDACY_REASON }),
      };
    }
    case "NOTE":
      return {
        destination: SHEET_NOTES,
        Icon: NoteCitationIcon,
        name: "Note",
      };
    case "STATUS_REASON":
      // While comments host the rejection status reasons right now, it's fragile,
      // so we opt for detail only until something more robust is in place.
      return { Icon: StatusChangeCitationIcon, name: "Status change" };
    case "EDIT":
      return {
        Icon: EditCitationIcon,
        name:
          row.touchpointPrincipalType === "STAFFER"
            ? "Staffer edit"
            : "User edit",
      };
    case "EVALUATION":
      // Not supported right now.
      return { Icon: EvaluationCitationIcon, name: "Evaluation" };
    case "RESUME":
      // Not supported right now. NOTE: There is a chance that a paper trail refers
      // to a resume that has been deleted from the candidate sheet. Need to figure
      // out what to do in that case.
      return {
        destination: SHEET_RECORDS,
        Icon: ResumeCitationIcon,
        name: "Resume",
      };
    default: {
      // Tolerate touchpoint types the backend adds before this enum catches up.
      // Sentence case to match the hand-written labels above.
      const words = row.touchpointType.toLowerCase().split("_").join(" ");
      return {
        Icon: CommentsCitationIcon,
        name: words.charAt(0).toUpperCase() + words.slice(1),
      };
    }
  }
};

const getQuote = (detail: unknown): string | null => {
  if (typeof detail !== "object" || detail === null) return null;
  const quote = (detail as { quote?: unknown }).quote;
  return typeof quote === "string" && quote.length ? quote : null;
};

// `detail` is jsonb and its shape varies by touchpointType, so every read below
// re-checks at runtime. Rows written before the shape was formalised carry only
// quote and confidence — absence is normal, not an error.
const asDetail = (detail: unknown): Record<string, unknown> | null =>
  typeof detail === "object" && detail !== null
    ? (detail as Record<string, unknown>)
    : null;

// The conversation around the quote, on CALL / SMS / EMAIL.
const getThread = (detail: unknown): SpeakerThreadTurn[] | null => {
  const context = asDetail(detail)?.quoteContext;
  if (!Array.isArray(context)) return null;

  const turns = context.flatMap((turn): SpeakerThreadTurn[] => {
    const candidate = asDetail(turn);
    const speaker = candidate?.speaker;
    const text = candidate?.text;
    if (typeof text !== "string" || !text.length) return [];
    if (speaker !== "INTERNAL" && speaker !== "USER") return [];
    return [{ speaker, text }];
  });

  return turns.length ? turns : null;
};

// The form field an application answer came from. Same key as the thread, a
// different shape — a question is not an utterance, so it is not a turn.
// Stored as { Q, A } on the wire (see nova's applicationDetailSchema); renamed
// to full words at this boundary so the rest of the FE reads plainly.
const getQuestionAnswer = (
  detail: unknown,
): { answer: string; question: string } | null => {
  const context = asDetail(detail)?.quoteContext;
  const pair = asDetail(context);
  if (!pair) return null;
  const { A: answer, Q: question } = pair;
  if (typeof answer !== "string" || typeof question !== "string") return null;
  if (!answer.length && !question.length) return null;
  return { answer, question };
};

// Which candidacy a candidacy-scoped artifact belongs to.
const getDetailCandidacyId = (detail: unknown): string | null => {
  const candidacyId = asDetail(detail)?.candidacyId;
  return typeof candidacyId === "string" && candidacyId.length
    ? candidacyId
    : null;
};

// All three ids, which is what a link to the other candidacy's sheet needs:
// company and job build the path, candidacy is the ?c= param. Any one missing
// means no link — a partial URL would land somewhere wrong rather than nowhere.
const getDetailCandidacyScope = (
  detail: unknown,
): { candidacyId: string; companyId: string; jobId: string } | null => {
  const scope = asDetail(detail);
  const candidacyId = scope?.candidacyId;
  const companyId = scope?.companyId;
  const jobId = scope?.jobId;
  if (
    typeof candidacyId !== "string" ||
    typeof companyId !== "string" ||
    typeof jobId !== "string" ||
    !candidacyId.length ||
    !companyId.length ||
    !jobId.length
  ) {
    return null;
  }
  return { candidacyId, companyId, jobId };
};

// Which field the edit was about. Null when the row names no field, which keeps
// the label from rendering a stray separator.
const getFieldLabel = (row: PaperTrailCitationRow): string | null => {
  if (row.shift != null) return SHIFT_LABELS[row.shift] ?? row.shift;
  if (!row.entityField) return null;
  return FIELD_LABELS[row.entityField] ?? row.entityField;
};

// The staffer behind a citation. Only STAFFER-principal rows have one — agent
// and candidate-authored rows are attributed by their type label alone, so an
// id sitting on one of those is not an author and is deliberately ignored.
// Null when we hold no name for the id: the caller's generic label is better
// than a bare number.
const getStafferName = (
  row: PaperTrailCitationRow,
  stafferNamesById?: Map<number, string>,
): string | null => {
  if (row.touchpointPrincipalType !== "STAFFER") return null;
  if (row.stafferId == null) return null;
  const name = stafferNamesById?.get(row.stafferId);
  return name?.length ? name : null;
};

// Who made the edit. A staffer we can't name still reads better as "Staffer
// edit" than as a bare id.
const getEditAuthor = (
  row: PaperTrailCitationRow,
  stafferNamesById?: Map<number, string>,
): string => {
  if (row.touchpointPrincipalType === "USER") return "Self-edit";
  return getStafferName(row, stafferNamesById) ?? "Staffer edit";
};

// The value a row actually wrote. `value` is jsonb, so each branch re-checks
// the runtime type rather than trusting entityField to imply it.
const getSetValue = (row: PaperTrailCitationRow): string | null => {
  const { entityField, value } = row;
  if (value == null) return null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") {
    if (entityField === "currentPay" || entityField === "desiredPay") {
      return formatting.payWithUnit(value);
    }
    if (entityField === "maxCommuteMiles") return `${value.toString()} mi`;
    return value.toString();
  }
  if (typeof value !== "string") return null;
  if (row.shift != null) return SHIFT_STATUS_LABELS[value] ?? value;
  if (entityField === "drugScreenStatus") {
    return DRUG_SCREEN_VALUE_LABELS[value] ?? value;
  }
  if (entityField === "earliestStartDate") {
    const startDate = new Date(value);
    if (Number.isNaN(startDate.getTime())) return value;
    return SHORT_DATE_FORMAT.format(startDate);
  }
  return value;
};

// Shift rows arrive in write order, which is arbitrary to a reader. Rank them
// by stance then shift; everything else is unranked and keeps its order, since
// Array.prototype.sort is stable.
const UNRANKED = Number.MAX_SAFE_INTEGER;
const getStanceRank = (row: PaperTrailCitationRow): [number, number] => {
  if (row.shift == null) return [UNRANKED, UNRANKED];
  const statusIndex =
    typeof row.value === "string" ? SHIFT_STATUS_ORDER.indexOf(row.value) : -1;
  const shiftIndex = SHIFT_ORDER.indexOf(row.shift);
  return [
    statusIndex === -1 ? UNRANKED : statusIndex,
    shiftIndex === -1 ? UNRANKED : shiftIndex,
  ];
};

const sortByStance = (
  rows: PaperTrailCitationRow[],
): PaperTrailCitationRow[] => {
  return [...rows].sort((firstRow, secondRow) => {
    const [firstStatus, firstShift] = getStanceRank(firstRow);
    const [secondStatus, secondShift] = getStanceRank(secondRow);
    return firstStatus - secondStatus || firstShift - secondShift;
  });
};

// The values a touchpoint wrote. An edit names its field in the source label
// already, so it carries the bare value; an artifact labels each with the field
// or shift it settled, since one quote can settle several.
const buildChanges = (
  rows: PaperTrailCitationRow[],
  { withFieldLabels }: { withFieldLabels: boolean },
): PaperTrailCitationChange[] | null => {
  const changes = sortByStance(rows).flatMap(
    (row): PaperTrailCitationChange[] => {
      const value = getSetValue(row);
      if (value == null) return [];
      if (!withFieldLabels) return [{ value }];
      const fieldLabel = getFieldLabel(row);
      return [{ label: fieldLabel ?? undefined, value }];
    },
  );
  return changes.length ? changes : null;
};

// Each distinct quote in an artifact, in the order the server returned them,
// with every row that quote wrote folded together. A shift statement writes one
// row per shift off a single quote; a text can also state two different things.
const toClaims = (
  rows: PaperTrailCitationRow[],
  { withFieldLabels }: { withFieldLabels: boolean },
): PaperTrailCitationClaim[] => {
  const rowsByQuote = new Map<string, PaperTrailCitationRow[]>();
  for (const row of rows) {
    // "" keys the quote-less rows together, which is right: they are all the
    // same artifact saying nothing citable.
    const quote = getQuote(row.detail) ?? "";
    // An application asks many questions, and short answers repeat across them
    // — two questions both answered "Yes" are different facts, so the question
    // joins the key. Conversation evidence has one context per touchpoint and
    // groups on the quote alone.
    const question = getQuestionAnswer(row.detail)?.question ?? "";
    const key = `${quote}\u0000${question}`;
    const existingRows = rowsByQuote.get(key);
    if (existingRows) existingRows.push(row);
    else rowsByQuote.set(key, [row]);
  }
  return [...rowsByQuote.values()].map((quoteRows) => {
    // Every row in a group shares a quote and a question, so its context and
    // quote can be read from the first.
    const [firstRow] = quoteRows;
    const quote = getQuote(firstRow.detail) ?? "";
    const questionAnswer = getQuestionAnswer(firstRow.detail);
    return {
      changes: buildChanges(quoteRows, { withFieldLabels }) ?? undefined,
      id: firstRow.id,
      questionAnswer: questionAnswer ?? undefined,
      quote: quote || undefined,
      thread: getThread(firstRow.detail) ?? undefined,
    };
  });
};

// One artifact can settle several targets at once — a single "day shift only"
// text writes a row per shift, and each shift then cites it separately. Every
// row from one touchpoint collapses into one entry, however many quotes it
// carries. Edits have no touchpointId and so never group: each is a deliberate,
// separate change.
// A thread's internal turns are labelled by who owned the exchange: the agent
// on an agent line, otherwise the recruiter.
const getInternalLabel = (
  row: PaperTrailCitationRow,
  stafferNamesById?: Map<number, string>,
): string => {
  if (row.touchpointPrincipalType === "AGENT") return "AI recruiter";
  return getStafferName(row, stafferNamesById) ?? "Recruiter";
};

const getGroupKey = (row: PaperTrailCitationRow): string | null => {
  if (!row.touchpointId) return null;
  return row.touchpointId;
};

const groupRows = (
  rows: PaperTrailCitationRow[],
): PaperTrailCitationRow[][] => {
  const groups: PaperTrailCitationRow[][] = [];
  const groupsByKey = new Map<string, PaperTrailCitationRow[]>();
  for (const row of rows) {
    const key = getGroupKey(row);
    if (key == null) {
      groups.push([row]);
      continue;
    }
    const existingGroup = groupsByKey.get(key);
    if (existingGroup) {
      existingGroup.push(row);
      continue;
    }
    // Pushed into both, so later rows extend the group in place and the
    // server's newest-first ordering survives.
    const newGroup = [row];
    groupsByKey.set(key, newGroup);
    groups.push(newGroup);
  }
  return groups;
};

/**
 * Trail rows. Renderable citation sources, preserving the server's
 * newest-conversation-first order, with rows from one artifact grouped into a
 * single entry. Rows without a quote still render — they are provenance events
 * (edits, resume/evaluation attribution), which name their field and author in
 * the label instead.
 *
 * `stafferNamesById` maps guac staffer ids to display names; without it edits
 * fall back to the generic "Staffer edit".
 */
// AI-call-insight citations are synthetic (no paper-trail row behind them), so
// they carry a sentinel id. Nova row ids are positive, so this never collides.
const AI_CALL_INSIGHT_ID = -1;

/**
 * A citation lifted from the latest Stevie call summary rather than the paper
 * trail. Rendered identically to a CALL extraction citation, but display-only:
 * nothing behind it was written to nova. `changes` optionally badges the
 * value(s) the quote supports, for rows whose main value comes from elsewhere.
 */
export const toAiCallInsightCitationSource = (
  citation: AiCallInsightCitation | null | undefined,
  { changes }: { changes?: PaperTrailCitationChange[] } = {},
): PaperTrailCitationSource[] => {
  if (!citation) return [];
  // Guac dates are ISO with timezone, unlike nova's tz-naive timestamps.
  const callDate = new Date(citation.callAt);
  if (Number.isNaN(callDate.getTime())) return [];
  // Already in thread vocabulary (guac normalizes); just drop empty turns.
  const thread = citation.quoteContext.filter((turn) => turn.text.length);
  return [
    {
      claims: [
        {
          changes,
          id: AI_CALL_INSIGHT_ID,
          quote: citation.quote,
          thread: thread.length ? thread : undefined,
        },
      ],
      destination: SHEET_RECORDS,
      Icon: AgenticCallCitationIcon,
      id: AI_CALL_INSIGHT_ID,
      internalLabel: "AI recruiter",
      label: ["AI call", SHORT_DATE_FORMAT.format(callDate)].join(" · "),
      sortKey: callDate.getTime(),
    },
  ];
};

export const toCitationSources = (
  rows: PaperTrailCitationRow[],
  {
    currentCandidacyId,
    stafferNamesById,
  }: {
    currentCandidacyId?: string;
    stafferNamesById?: Map<number, string>;
  } = {},
): PaperTrailCitationSource[] => {
  return groupRows(rows).flatMap((group) => {
    // Groups are built one row at a time, so this is never empty.
    const [firstRow] = group;
    const { destination, Icon, name, tag, tagReason } = sourceKind(
      firstRow,
      currentCandidacyId,
    );
    // Nova timestamps arrive timezone-naive; parse as UTC so the label shows
    // the staffer's local day rather than the UTC wall-clock re-read as local.
    const touchpointDate = dates.parseUtcTimestamp(firstRow.touchpointAt);
    // An unparseable date would make Intl.DateTimeFormat.format THROW (not
    // just render badly) — drop the row rather than crash the section.
    if (Number.isNaN(touchpointDate.getTime())) return [];
    const isEdit = firstRow.touchpointType === "EDIT";
    const labelParts = isEdit
      ? [getFieldLabel(firstRow), getEditAuthor(firstRow, stafferNamesById)]
      : [name, getStafferName(firstRow, stafferNamesById)];
    return [
      {
        claims: toClaims(group, { withFieldLabels: !isEdit }),
        destination,
        Icon,
        id: firstRow.id,
        internalLabel: getInternalLabel(firstRow, stafferNamesById),
        label: [
          ...labelParts.filter((part): part is string => part != null),
          SHORT_DATE_FORMAT.format(touchpointDate),
        ].join(" · "),
        tag,
        tagReason,
        sortKey: touchpointDate.getTime(),
      },
    ];
  });
};
